<script setup lang="ts">
// 引入默认图片
import defaultImage from '~/assets/images/logo_gray.png'

interface Props {
  src?: string
  alt?: string
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  class?: string
  placeholder?: string // 占位图URL
  width?: number | string
  height?: number | string
  quality?: number | string
  format?: string
  showPreload?: boolean
  loading?: 'lazy' | 'eager'
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  alt: '',
  rounded: 'lg',
  class: '',
  placeholder: '',
  quality: 80,
  format: 'webp',
  showPreload: false,
  loading: 'lazy'
})

// 图片加载状态
const hasError = ref(false)
const isLoaded = ref(true)

// 处理图片加载完成
const onImageLoaded = () => {
  isLoaded.value = true
}

// 处理图片加载错误
const onImageError = () => {
  hasError.value = true
}

const roundedClass = computed(() => {
  const roundedMap = {
    'none': '',
    'sm': 'rounded-sm',
    'md': 'rounded-md',
    'lg': 'rounded-lg',
    'xl': 'rounded-xl',
    'full': 'rounded-full'
  }
  return roundedMap[props.rounded]
})

// 判断是否显示默认图片
const shouldShowDefault = computed(() => {
  return !props.src || hasError.value || !isLoaded.value
})

// 默认图片样式
const defaultImageClasses = computed(() => {
  return `relative z-10 w-1/2 h-1/2 object-contain m-auto ${roundedClass.value} ${props.class}`
})

// 实际图片样式
const actualImageClasses = computed(() => {
  return `relative z-10 w-full h-full object-cover ${roundedClass.value} ${props.class}`
})
</script>

<template>
  <div 
    class="relative w-full h-full overflow-hidden flex items-center justify-center" 
    :class="roundedClass"
  >
    <div class="absolute inset-0 bg-[rgba(0,0,0,0.5)]" :class="roundedClass"></div>
    
    <!-- 默认图片 (在实际图片加载完成前显示) -->
    <img 
      v-if="shouldShowDefault"
      :src="defaultImage"
      :alt="alt"
      :class="defaultImageClasses"
    />
    
    <!-- 实际图片 -->
    <NuxtImg 
      v-if="src && !hasError"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :quality="quality"
      :format="format"
      :placeholder="placeholder || undefined"
      :class="[actualImageClasses, {'opacity-0': !isLoaded, 'opacity-100': isLoaded}]"
      :style="{ transition: 'opacity 0.2s ease-in-out' }"
      provider="cloudflareCustom"
      :loading="loading"
      @load="onImageLoaded"
      @error="onImageError"
      :preload="showPreload"
    />
  </div>
</template> 